import React, { useEffect } from 'react'
import styled from 'styled-components'
import { MyTheme } from 'Components/common/Theme'
import { formatDateCustom, numberWithComma } from 'helpers/format'
import { BillProductData, Data } from 'Store/orders/types'
import { Col, Row, Space } from 'antd'
import { selectCurrentUser } from 'Store/user/selector'
import RootState from 'typings/RootState'
import { connect, useDispatch } from 'react-redux'
import { getPosBillAPI } from 'Store/orders/api'
import { BillReq } from 'Store/orders/types'
import { selectPosBill } from 'Store/orders/selector'
import { AuditOutlined } from '@ant-design/icons'
import { themeConfig } from 'config'
import { CurrentUser } from 'Store/user/types'
import { VAT_TYPE, VAT_TYPE_TEXT } from 'helpers/constanst'

type Props = ReduxProps & {
  detail?: Data
}

const ReceiptPDF: React.FunctionComponent<Props> = props => {
  const { detail, currentUser, billData } = props
  const hasVoid = billData?.status === 'void'
  const dispatch = useDispatch()
  const isExcludeVat = detail?.payment_detail?.vat_type === VAT_TYPE.EX
  const isTax = detail?.payment_detail.is_tax
  const vatType = detail?.payment_detail?.vat_type
  const vatAmount = billData?.summary?.vat_amount || 0

  useEffect(() => {
    if (!detail?.id || detail?.id > 0) {
      onGetDetail()
    }
  }, [detail?.id])

  const onGetDetail = (): void => {
    if (detail?.id && detail?.id <= 0) return
    const req: BillReq = {
      orderID: detail?.id || 0,
    }
    dispatch(getPosBillAPI(req, currentUser.accessToken))
  }

  const renderInfo = (val: any) => {
    return <RowTile>{val}</RowTile>
  }

  const renderInfoItem = (title: any, value: any, isBold?: boolean) => {
    return (
      <Row justify="space-between">
        <Col>{isBold ? <Textbold>{title}</Textbold> : <InfoTitle>{title}</InfoTitle>}</Col>
        <Col>{isBold ? <Textbold>{value || ''}</Textbold> : <InfoValue>{value || ''}</InfoValue>}</Col>
      </Row>
    )
  }

  const renderProductName = (item: BillProductData) => {
    const isSameTitle = item.title == item.product_option_title
    return (
      <>
        <RowTile>{item.title}</RowTile>
        {!isSameTitle && <ProductDesc>{item.product_option_title}</ProductDesc>}
      </>
    )
  }

  const renderTable = () => {
    return (
      <Table>
        <thead>
          <th className="left">#</th>
          <th className="left">สินค้า</th>
          <th className="right">จำนวน</th>
          <th className="center">
            <PaddingLeft>หน่วย</PaddingLeft>
          </th>
          <th className="right" style={{ width: '100px' }}>
            ราคา
          </th>
          <th className="right" style={{ width: '100px' }}>
            ราคารวม
          </th>
        </thead>
        <tbody>
          {billData?.products.map((item: BillProductData, index: number) => {
            return (
              <>
                <tr key={index}>
                  <td className="left">{index + 1}</td>
                  <td className="left">{renderProductName(item)}</td>
                  <td className="right">{renderInfo(`${numberWithComma(item?.quantity)}`)}</td>
                  <td className="center">
                    {<PaddingLeft>{renderInfo(item?.product_uom || '')}</PaddingLeft>}
                  </td>
                  <td className="right">
                    {renderInfo(numberWithComma(item.price_before_discount || 0, true))}
                  </td>
                  <td className="right">
                    {renderInfo(numberWithComma((item.price_before_discount || 0) * item.quantity, true))}
                  </td>
                </tr>
                {(item.discount || 0) > 0 && (
                  <tr key={index}>
                    <td className="left"></td>
                    <td className="left">ส่วนลด {numberWithComma(item.discount || 0)} บาท</td>
                    <td className="right"></td>
                    <td className="right"></td>
                    <td className="right">
                      {renderInfo(numberWithComma(-(item.discount || 0) * item.quantity, true))}
                    </td>
                  </tr>
                )}
              </>
            )
          })}
          <tr>
            <td className="left"></td>
            <td className="left">ยอดรวม</td>
            <td className="right">{renderInfo(numberWithComma(billData?.summary?.qty || 0))}</td>
            <td className="right"></td>
            <td className="right">
              {renderInfo(numberWithComma(billData?.summary?.amount_before_discount || 0, true))}
            </td>
          </tr>
        </tbody>
      </Table>
    )
  }

  const renderSenderInfo = () => {
    return (
      <Row justify="center">
        <Col span={18} style={{ textAlign: 'center' }}>
          <BoxTitle>{billData?.merchant?.name}</BoxTitle>
          <InfoValue>{billData?.merchant.address}</InfoValue>
          <InfoValue>โทรศัพท์ {currentUser?.activeMerchant?.phone}</InfoValue>
          <BoxTitle style={{ marginTop: '5px' }}>ใบเสร็จ</BoxTitle>
        </Col>
      </Row>
    )
  }

  const renderVoidInfo = () => {
    return (
      <>
        <LineDash />
        {renderInfoItem('ข้อมูลการยกเลิก', '')}
        {renderInfoItem('เหตุผล', billData?.reason || '')}
        {renderInfoItem('ชำระคืนโดย', billData?.staff_void.name)}
        {renderInfoItem('ยอดรวม', numberWithComma(billData?.tota_price, true))}
        {renderInfoItem('วันเวลายกเลิกบิล', formatDateCustom(billData?.void_date || '', 'DD/MM/YYYY HH:mm'))}
      </>
    )
  }

  return (
    <BoxWrapper>
      {renderSenderInfo()}
      <LineDash />
      {renderInfoItem('เลขที่บิล', billData?.bill_no)}
      {renderInfoItem('เลขที่', billData?.order_no)}
      {renderInfoItem('TerminalID', billData?.terminal.name)}
      {renderInfoItem('ชื่อพนักงาน', billData?.staff_cash.name)}
      {renderInfoItem('วันเวลา', formatDateCustom(billData?.payment_date || '', 'DD/MM/YYYY HH:mm'))}
      {renderTable()}
      <LineDash />
      {renderInfoItem(
        'ส่วนลดท้ายบิล',
        `${(billData?.summary?.total_discount_amount || 0) > 0 ? '-' : ''}${numberWithComma(
          billData?.summary?.total_discount_amount || 0,
          true,
        )}`,
      )}
      {renderInfoItem(
        'ยอดรวม',
        numberWithComma(
          (billData?.summary?.amount_before_discount || 0) - (billData?.summary?.total_discount_amount || 0),
          true,
        ),
      )}
      {isExcludeVat &&
        isTax &&
        renderInfoItem(
          `ภาษีมูลค่าเพิ่ม 7% (${vatType === VAT_TYPE.EX ? VAT_TYPE_TEXT.EX : VAT_TYPE_TEXT.IN})`,
          `${numberWithComma(vatAmount, true)}`,
        )}
      {renderInfoItem('รวมสุทธิ', numberWithComma(billData?.summary?.amount || 0, true), true)}
      {renderInfoItem('เงินสด', numberWithComma(billData?.cash || 0, true))}
      {renderInfoItem('เงินทอน', numberWithComma(billData?.change || 0, true))}
      {hasVoid && renderVoidInfo()}
      <br />
      <Row justify="center">
        {hasVoid ? (
          <Col span={24}>
            <LineRed />
            <TextCenter>
              <InfoValueGrey>
                <AuditOutlined /> บิลถูกยกเลิก
              </InfoValueGrey>
            </TextCenter>
            <LineRed />
          </Col>
        ) : (
          <Col>
            <InfoValueGrey>ขอบคุณลูกค้าทุกท่านที่มาอุดหนุน</InfoValueGrey>
            <InfoPowerBy>Powered by {(themeConfig.title.terminalName || '').toUpperCase()}</InfoPowerBy>
          </Col>
        )}
      </Row>
    </BoxWrapper>
  )
}

const LineDash = styled.div`
  width: 100%;
  border-bottom: 1px dashed #000;
  margin: 10px 0;
`

const LineRed = styled.div`
  width: 100%;
  border-bottom: 1px solid ${(props: { theme: MyTheme }): string => props.theme.colors.error};
  margin: 5px 0;
`

const BoxTitle = styled.div`
  font-size: ${(props: { theme: MyTheme }): string => props.theme.typography.body4};
  font-family: ${(props: { theme: MyTheme }): string => props.theme.fontFamily.bold};
`

const BoxWrapper = styled.div`
  width: 0;
  height: 0;
  overflow: hidden;
  background-color: #fff;

  @media print {
    margin: 0 auto;
    width: 500px;
    padding: 1cm;
    height: auto;
    margin-top: 20px;
  }
`

const RowTile = styled.div`
  font-size: ${(props: { theme: MyTheme }): string => props.theme.typography.body5};
`

const ProductDesc = styled(RowTile)`
  color: ${(props: { theme: MyTheme }): string => props.theme.colors.grey};
  font-size: ${(props: { theme: MyTheme }): string => props.theme.typography.body5};
`

const InfoTitle = styled.div`
  font-size: ${(props: { theme: MyTheme }): string => props.theme.typography.body5};
  color: ${(props: { theme: MyTheme }): string => props.theme.colors.black};
`

const InfoValue = styled.div`
  font-size: ${(props: { theme: MyTheme }): string => props.theme.typography.body5};
  color: ${(props: { theme: MyTheme }): string => props.theme.colors.black};
`

const InfoValueGrey = styled(InfoValue)`
  color: #6d6e70;
`

const InfoPowerBy = styled(InfoValue)`
  color: #6d6e70;
  font-size: 8px;
  text-align: center;
`

const TextCenter = styled.div`
  text-align: center;
`

const Textbold = styled.div`
  font-size: ${(props: { theme: MyTheme }): string => props.theme.typography.body4};
  font-family: ${(props: { theme: MyTheme }): string => props.theme.fontFamily.bold};
  color: ${(props: { theme: MyTheme }): string => props.theme.colors.black};
`

const Table = styled.table`
  width: 100%;
  margin: 10px 0;
  font-size: ${(props: { theme: MyTheme }): string => props.theme.typography.body5};
  th,
  td {
    border: none;
    vertical-align: top;
    text-align: center;
  }
  th {
    border: none;
    vertical-align: middle;
    border-bottom: 1px dashed #000;
    border-top: 1px dashed #000;
    padding: 5px 0;
  }

  .right {
    text-align: right;
  }
  .left {
    text-align: left;
  }
  .center {
    text-align: center;
  }
`

const PaddingLeft = styled.div`
  padding-left: 5px;
`

const mapStateToProps = (state: RootState) => {
  return {
    currentUser: selectCurrentUser(state),
    billData: selectPosBill(state),
  }
}

type ReduxProps = ReturnType<typeof mapStateToProps>

export default connect(mapStateToProps)(ReceiptPDF)
